// Data for the newer, data-driven service pages (built alongside the
// original hand-coded service pages: Prefab.js, SteelFabrication.js, etc.)

export const newServices = [
  {
    slug: "peb-warehouse",
    path: "/services/peb-warehouse",
    name: "PEB Warehouse Construction",
    tagline: "Purpose-built warehouses engineered for storage density and material flow.",
    intro:
      "A warehouse isn't just a large shed — it's a structure designed around clear height, column spacing and floor loading that directly determine how much you can store and how efficiently goods move through it. We design PEB warehouses starting from your racking and material-handling plan, not a generic footprint.",
    applications: [
      "Logistics & distribution centres",
      "Manufacturing-linked storage warehouses",
      "Third-party logistics (3PL) facilities",
      "Cold-chain adjacent storage (with insulated sections)",
      "E-commerce fulfilment centres",
    ],
    benefits: [
      { title: "Maximized Clear Height", text: "Structural design optimized for multi-level racking within your budget." },
      { title: "Column-Efficient Layout", text: "Wide spans planned around forklift and material-handling equipment turning radius." },
      { title: "Fast Turnaround", text: "PEB construction typically completes 50-65% faster than equivalent RCC warehousing." },
      { title: "Dock & Loading Design", text: "Dock door count and yard layout planned for your actual truck throughput." },
    ],
    process: [
      { title: "Requirement Study", text: "We understand your storage volume, racking system and throughput needs." },
      { title: "Structural Design", text: "Clear height, span and floor loads engineered to your specification." },
      { title: "Fabrication", text: "Steel fabricated at our facility while foundation work proceeds in parallel." },
      { title: "Erection & Handover", text: "On-site erection through to a handover-ready warehouse shell." },
    ],
    faqs: [
      { q: "What factors affect warehouse construction cost the most?", a: "Clear height, floor load rating, soil conditions, insulation needs and dock/loading specifications typically move the price more than square footage alone." },
      { q: "Can you design around our existing racking system plan?", a: "Yes — sharing your racking layout early lets us coordinate column spacing to avoid obstructions in your aisles." },
    ],
  },
  {
    slug: "peb-factory-buildings",
    path: "/services/peb-factory-buildings",
    name: "PEB Factory Buildings",
    tagline: "Manufacturing facilities engineered around your production line, not a generic template.",
    intro:
      "Factory buildings need to support the actual manufacturing process — machinery placement, crane access, floor loading and ventilation all depend on what's being produced. We design PEB factory buildings around your specific production requirements, from initial layout through erection and project management.",
    applications: [
      "General manufacturing facilities",
      "Auto-ancillary & component manufacturing",
      "Engineering & fabrication workshops",
      "Process-industry manufacturing units",
      "Assembly and production plants",
    ],
    benefits: [
      { title: "Process-Driven Design", text: "Column spacing and clear span planned around your production line layout." },
      { title: "Crane-Ready Structures", text: "EOT and gantry crane provisions designed in from the start where needed." },
      { title: "Heavy-Duty Flooring", text: "VDF flooring specified for machinery point loads and equipment traffic." },
      { title: "Project Management", text: "Design, fabrication, roofing, cladding and erection managed as one coordinated project." },
    ],
    process: [
      { title: "Design", text: "Layout and structural engineering around your production process." },
      { title: "Structural Engineering", text: "Load calculations for machinery, crane and site-specific wind/seismic conditions." },
      { title: "Fabrication", text: "Precision fabrication of primary and secondary steel members." },
      { title: "Roofing & Cladding", text: "Roof and wall sheeting specified for your thermal and durability needs." },
      { title: "Erection", text: "On-site assembly through to a production-ready facility." },
      { title: "Project Management", text: "Coordinated scheduling across design, fabrication and site work." },
    ],
    faqs: [
      { q: "Can a factory building be designed with a crane system?", a: "Yes — EOT or gantry crane provisions need to be planned at the design stage, since retrofitting crane capacity into a structure not originally designed for it is often impractical." },
      { q: "Do you handle statutory approvals for factory construction?", a: "We support the technical documentation needed for approvals; the specific application process is typically coordinated with the relevant Telangana or AP authorities for your site." },
    ],
  },
  {
    slug: "peb-erection",
    path: "/peb-erection",
    name: "PEB Erection Services",
    tagline: "Professional on-site assembly, from anchor bolt verification to final handover.",
    intro:
      "Erection is where fabricated steel becomes an actual building — and it's a stage where site-readiness issues (misaligned anchor bolts, incomplete foundation curing) cause most of the delays that aren't related to the steel itself. Our erection process is built around catching these issues before they become expensive.",
    applications: [
      "Erection of PEB structures we've designed and fabricated",
      "Erection support for structures fabricated elsewhere (project-dependent)",
      "Re-erection or extension of existing PEB structures",
    ],
    benefits: [
      { title: "Pre-Erection Site Checks", text: "Anchor bolt alignment verified against drawings before erection begins." },
      { title: "Sequenced Assembly", text: "Primary frames, secondary members, bracing and sheeting installed in the correct load-bearing sequence." },
      { title: "Safety-First Process", text: "Erection follows standard safety protocols for working at height and crane operations." },
      { title: "Handover-Ready Finish", text: "Flashing, gutters and finishing details completed as part of the erection scope." },
    ],
    process: [
      { title: "Site Readiness Check", text: "Foundation curing and anchor bolt alignment verified against fabrication drawings." },
      { title: "Primary Frame Erection", text: "Columns and rafters lifted, bolted and plumbed." },
      { title: "Secondary Members & Bracing", text: "Purlins, girts and bracing installed for full structural stability." },
      { title: "Sheeting & Finishing", text: "Roof and wall sheeting, flashing and gutters completed." },
    ],
    faqs: [
      { q: "What happens if anchor bolts are found misaligned during the site check?", a: "Minor misalignment can sometimes be corrected with slotted base plate holes; significant misalignment may require foundation rework, which is why we check before erection begins rather than after steel arrives." },
    ],
  },
  {
    slug: "design-engineering",
    path: "/design-engineering",
    name: "Design & Engineering",
    tagline: "Structural design that starts from your actual requirements, not a generic template.",
    intro:
      "Good PEB design isn't about picking a span and height — it's a structured process of load calculation, section optimization and code compliance that determines how well the finished structure actually performs. Our design and engineering process is where we ensure your structure is right before a single piece of steel is cut.",
    applications: [
      "New PEB structure design",
      "Structural design for crane-equipped buildings",
      "Site-specific wind & seismic load engineering",
      "Design for future expansion capability",
      "Mezzanine and platform structural design",
    ],
    benefits: [
      { title: "Site-Specific Load Design", text: "Wind and seismic loads calculated for your actual site location per IS 875, not a generic assumption." },
      { title: "Section Optimization", text: "Steel sections tapered to actual load paths, minimizing tonnage and cost without compromising safety." },
      { title: "Code-Compliant Documentation", text: "Detailed fabrication drawings produced in line with applicable Indian design codes." },
      { title: "Future-Proofed Options", text: "Expansion capability designed in from the start where relevant, avoiding costly retrofits later." },
    ],
    process: [
      { title: "Functional Requirements", text: "Understanding span, height, crane loads and layout needs specific to your operation." },
      { title: "Load Calculations", text: "Dead, live, wind and seismic loads calculated per applicable codes for your site." },
      { title: "Section Optimization", text: "Structural software tapers steel sections to the actual bending-moment diagram." },
      { title: "Fabrication Drawings", text: "Detailed shop drawings produced for fabrication and erection teams." },
    ],
    faqs: [
      { q: "Which design codes do you follow?", a: "Primarily IS 800 (general steel construction) and IS 875 (design loads including wind and seismic), along with relevant codes for connections and cold-formed sections." },
      { q: "Can design account for a phase-2 expansion later?", a: "Yes, if planned upfront — end walls can be designed as removable/expandable bays to support future expansion without major structural rework." },
    ],
  },
];

export const getNewServiceBySlug = (slug) => newServices.find((s) => s.slug === slug);
